import type { ReactNode, SVGAttributes } from 'react';

import { ExternalLinkIcon } from 'lucide-react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';

import { cn } from '@/libraries/tailwind/cn';

export function ContentGridLink({
  href,
  title,
  subtitle,
  icon,
  className,
  iconClassName,
}: Readonly<{
  href: string;
  title: string;
  className?: string;
  subtitle?: string;
  iconClassName?: string;
  icon: (props: SVGAttributes<SVGSVGElement>) => ReactNode;
}>) {
  const pathname = usePathname();

  const isExternal = href.startsWith('http');
  const Component = isExternal ? 'a' : Link;
  const Icon = icon;

  const isActive = pathname === href;

  return (
    <li className={className}>
      <Component
        id={`${title}-Button`}
        data-umami-event={`${title}-Button`}
        href={href}
        {...(isExternal && { rel: 'noopener noreferrer', target: '_blank' })}
        className={cn(
          'group flex items-center p-2 py-2.5 gap-4 hover:bg-primary/5 rounded-lg',
          {
            'outline-gray-500 outline-1': isActive,
          },
        )}
      >
        {
          <div className={'w-[40px] flex justify-center'}>
            <Icon
              className={cn(
                `size-8 text-gray-400/60 group-hover:text-gray-600 ${iconClassName ?? ''}`,
                {
                  'text-gray-500': isActive,
                },
              )}
            />
          </div>
        }
        <div className="flex w-full flex-col text-sm whitespace-nowrap">
          <div className="text-gray-500 flex items-center gap-2 group-hover:text-gray-800">
            <p>{title}</p>
            {isExternal && <ExternalLinkIcon className="size-3" />}
          </div>
          {subtitle ? (
            <p className="text-muted-foreground">{subtitle}</p>
          ) : null}
        </div>
      </Component>
    </li>
  );
}
